<template>
    <div v-if="loading" class="public-loader">
        <div class="public-spinner" />
    </div>
    <div v-if="!loading" class="public-page">

        <!-- NAV -->
        <nav class="nav" :class="{ scrolled: isScrolled }">
            <div class="nav-inner">
                <div class="nav-logo">
                    <a href="#home">
                        <img src="/img/baner2.png" alt="Logo" class="nav-logo-img"
                            onerror="this.style.display='none'" />
                    </a>
                </div>
                <ul class="nav-links">
                    <li><a href="#home" @click.prevent="scrollTo('home')">{{ $t('publicPage.nav.home') }}</a></li>
                    <li><a href="#features" @click.prevent="scrollTo('features')">{{ $t('publicPage.nav.features')
                            }}</a></li>
                    <li><a href="#how" @click.prevent="scrollTo('how')">{{ $t('publicPage.nav.how') }}</a></li>
                    <li><a href="#contact" @click.prevent="scrollTo('contact')">{{ $t('publicPage.nav.contact') }}</a>
                    </li>
                </ul>

                <button class="nav-hamburger" @click="menuOpen = !menuOpen" :class="{ open: menuOpen }">
                    <span></span>
                    <span></span>
                    <span></span>
                </button>

                <!-- Mobile overlay menu -->
                <div class="nav-mobile-menu" :class="{ open: menuOpen }" @click="menuOpen = false">
                    <ul>
                        <li><a href="#home" @click="menuOpen = false">{{ $t('publicPage.nav.home') }}</a></li>
                        <li><a href="#features" @click="menuOpen = false">{{ $t('publicPage.nav.features') }}</a></li>
                        <li><a href="#how" @click="menuOpen = false">{{ $t('publicPage.nav.how') }}</a></li>
                        <li><a href="#contact" @click="menuOpen = false">{{ $t('publicPage.nav.contact') }}</a></li>
                    </ul>
                </div>

                <a href="/login" class="nav-btn">{{ $t('publicPage.nav.login') }}</a>

            </div>
        </nav>

        <!-- HERO -->
        <section class="hero" id="home">
            <div class="hero-bg">
                <div class="orb orb1"></div>
                <div class="orb orb2"></div>
                <div class="grid-overlay"></div>
            </div>
            <div class="hero-content">
                <div class="hero-badge">
                    <span class="badge-dot"></span>
                    {{ $t('publicPage.hero.badge') }}
                </div>
                <h1 class="hero-title">
                    <p style="margin-bottom: 10px;">{{ $t('publicPage.hero.title1') }}</p>
                    <p class="hero-gradient">{{ $t('publicPage.hero.title2') }}</p>
                    {{ $t('publicPage.hero.title3') }}
                </h1>
                <p class="hero-sub">{{ $t('publicPage.hero.sub') }}</p>
               
                <div class="hero-btns">
                    <a href="/login" class="btn-primary">{{ $t('publicPage.hero.btnPrimary') }}</a>
                    <a href="#features" class="btn-outline">{{ $t('publicPage.hero.btnOutline') }}</a>
                </div>
                <div class="hero-stats">
                    <div class="hstat">
                        <span class="hstat-v">{{ $t('publicPage.hero.stat1Value') }}</span>
                        <span class="hstat-l">{{ $t('publicPage.hero.stat1Label') }}</span>
                    </div>
                    <div class="hstat-div"></div>
                    <div class="hstat">
                        <span class="hstat-v">{{ $t('publicPage.hero.stat2Value') }}</span>
                        <span class="hstat-l">{{ $t('publicPage.hero.stat2Label') }}</span>
                    </div>
                    <div class="hstat-div"></div>
                    <div class="hstat">
                        <span class="hstat-v">{{ $t('publicPage.hero.stat3Value') }}</span>
                        <span class="hstat-l">{{ $t('publicPage.hero.stat3Label') }}</span>
                    </div>
                </div>
            </div>
            <div class="hero-visual">
                <div class="mock-card">
                    <div class="mock-topbar">
                        <div class="mock-dots"><span></span><span></span><span></span></div>
                        <span class="mock-label">{{ $t('publicPage.mock.topbarLabel') }}</span>
                    </div>
                    <div class="mock-table-wrap">
                        <div class="mock-thead">
                            <span>{{ $t('publicPage.mock.colInv') }}</span>
                            <span>{{ $t('publicPage.mock.colName') }}</span>
                            <span>{{ $t('publicPage.mock.colLoc') }}</span>
                            <span>{{ $t('publicPage.mock.colStatus') }}</span>
                            <span style="margin-left: 10px;">{{ $t('publicPage.mock.colTkv') }}</span>
                        </div>
                        <div class="mock-trow" v-for="r in $tm('publicPage.mock.rows')" :key="r.inv">
                            <span class="inv-badge">{{ r.inv }}</span>
                            <span>{{ r.name }}</span>
                            <span class="loc">{{ r.loc }}</span>
                            <span><em :class="'stag stag-' + r.sc">{{ r.sl }}</em></span>
                            <span class="tkv">{{ r.tkv }}</span>
                        </div>
                    </div>
                    <div class="mock-footer-bar">
                        <span class="mfb-chip">{{ $t('publicPage.mock.chip1') }}</span>
                        <span class="mfb-chip">{{ $t('publicPage.mock.chip2') }}</span>
                        <span class="mfb-chip">{{ $t('publicPage.mock.chip3') }}</span>
                    </div>
                </div>
            </div>
        </section>

        <!-- FEATURES -->
        <section class="features-section" id="features">
            <div class="section-wrap">
                <div class="section-head">
                    <span class="sec-badge">{{ $t('publicPage.features.badge') }}</span>
                    <h2 class="sec-title">
                        {{ $t('publicPage.features.title1') }}<br />
                        <span class="hero-gradient">{{ $t('publicPage.features.title2') }}</span>
                    </h2>
                    <p class="sec-sub">{{ $t('publicPage.features.sub') }}</p>
                </div>
                <div class="feat-grid">
                    <div class="feat-card" v-for="f in $tm('publicPage.features.items')" :key="f.title">
                        <div class="feat-icon">{{ f.icon }}</div>
                        <h3 class="feat-title">{{ f.title }}</h3>
                        <p class="feat-desc">{{ f.desc }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- HOW IT WORKS -->
        <section class="how-section" id="how">
            <div class="section-wrap">
                <div class="section-head">
                    <span class="sec-badge">{{ $t('publicPage.how.badge') }}</span>
                    <h2 class="sec-title">
                        {{ $t('publicPage.how.title1') }}
                        <span class="hero-gradient">{{ $t('publicPage.how.title2') }}</span>
                    </h2>
                    <p class="sec-sub">{{ $t('publicPage.how.sub') }}</p>
                </div>
                <div class="steps-row">
                    <div class="step-item" v-for="(s, i) in $tm('publicPage.how.steps')" :key="s.label">
                        <div class="step-circle">
                            <span class="step-emoji">{{ s.icon }}</span>
                            <span class="step-num">{{ i + 1 }}</span>
                        </div>
                        <div class="step-info">
                            <div class="step-title">{{ s.label }}</div>
                            <div class="step-desc">{{ s.desc }}</div>
                        </div>
                        <div class="step-connector" v-if="i < $tm('publicPage.how.steps').length - 1"></div>
                    </div>
                </div>
            </div>
        </section>

        <!-- HIGHLIGHT STRIP -->
        <section class="strip-section">
            <div class="section-wrap">
                <div class="strip-grid">
                    <div class="strip-item" v-for="s in $tm('publicPage.strips.items')" :key="s.title">
                        <span class="strip-icon">{{ s.icon }}</span>
                        <div>
                            <div class="strip-title">{{ s.title }}</div>
                            <div class="strip-desc">{{ s.desc }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CONTACT -->
        <section class="contact-section" id="contact">
            <div class="section-wrap contact-wrap">
                <div class="contact-left">
                    <span class="sec-badge">{{ $t('publicPage.contact.badge') }}</span>
                    <h2 class="sec-title">
                        {{ $t('publicPage.contact.title1') }}<br />
                        <span class="hero-gradient">{{ $t('publicPage.contact.title2') }}</span>
                    </h2>
                    <p class="sec-sub">{{ $t('publicPage.contact.sub') }}</p>
                     <p class="sec-sub">{{ $t('publicPage.contact.email') }}</p>
                    <!--
                    <div class="contact-info-list">
                        <div class="cinfo"><span>📧</span> info@proasseto.info</div>
                    </div>
                    -->
                </div>
                <div class="contact-right">
                    <form class="contact-form" @submit.prevent="sendEmail">
                        <div class="form-row">
                            <div class="form-group">
                                <label>{{ $t('publicPage.contact.labelName') }}</label>
                                <input type="text" :placeholder="$t('publicPage.contact.placeholderName')"
                                    v-model="form.name" name="name" />
                            </div>
                            <div class="form-group">
                                <label>{{ $t('publicPage.contact.labelEmail') }}</label>
                                <input type="email" v-model="form.email" name="email" />
                            </div>
                        </div>
                        <div class="form-group">
                            <label>{{ $t('publicPage.contact.labelCompany') }}</label>
                            <input type="text" :placeholder="$t('publicPage.contact.placeholderCompany')"
                                v-model="form.company" name="company" />
                        </div>
                        <div class="form-group">
                            <label>{{ $t('publicPage.contact.labelMessage') }}</label>
                            <textarea :placeholder="$t('publicPage.contact.placeholderMessage')" rows="4"
                                v-model="form.message" name="message"></textarea>
                        </div>

                        <button type="submit" class="btn-primary form-submit">{{ $t('publicPage.contact.btnSubmit')
                            }}</button>
                    </form>
                </div>
            </div>
        </section>

        <!-- FOOTER -->
        <footer class="footer-bar">
            <div class="footer-inner">
                <div class="footer-left">
                    <img src="/img/baner2.png" alt="" class="footer-logo-img" onerror="this.style.display='none'" />
                    <span class="footer-sep">|</span>
                    <span class="footer-tagline">{{ $t('publicPage.footer.tagline') }}</span>
                </div>
                <span class="footer-copy">© {{ year }}. {{ $t('publicPage.footer.copy') }}</span>
            </div>
        </footer>

    </div>
</template>

<script>
import emailjs from '@emailjs/browser'

export default {
    name: 'PublicPage',

    data() {
        return {
            isScrolled: false,
            loading: true,
            menuOpen: false,
            form: { name: '', email: '', company: '', message: '' },
        }
    },

    computed: {
        year() {
            return new Date().getFullYear()
        },
    },

    async mounted() {
        localStorage.removeItem('Token')

        const manuallySet = sessionStorage.getItem('localeManuallySet')
        const savedLocale = localStorage.getItem('locale')

        if (manuallySet && savedLocale) {
            this.$i18n.locale = savedLocale
        } else {
            await this.detectUserCountry()
        }

        this.loading = false

        window.addEventListener('scroll', this.onScroll)

        // Scroll na hash nakon učitavanja
        this.$nextTick(() => {
            const hash = window.location.hash
            if (hash) {
                const el = document.querySelector(hash)
                if (el) el.scrollIntoView({ behavior: 'smooth' })
            }
        })
    },

    beforeUnmount() {
        window.removeEventListener('scroll', this.onScroll)
    },

    methods: {
        scrollTo(id) {
            const el = document.getElementById(id)
            if (el) el.scrollIntoView({ behavior: 'smooth' })
        },
        async detectUserCountry() {
            try {
                const res = await fetch('https://api.country.is/')
                const data = await res.json()

                const country = data?.country

                //console.log('Detected country:', country)

                // Logika jezika
                if (country === 'BA') {
                    // BiH -> latinica
                    this.setLocale('lat')
                } else if (country === 'RS') {
                    // Srbija -> ekavica latinica
                    this.setLocale('lat-ekavica')
                } else {
                    // Ostalo -> engleski
                    this.setLocale('en')
                }

            } catch (err) {
                // fallback
                this.setLocale('en')
            }
        },
        onScroll() {
            this.isScrolled = window.scrollY > 50
        },

        setLocale(locale) {
            this.$i18n.locale = locale
            sessionStorage.setItem('locale', locale)
            sessionStorage.setItem('localeManuallySet', 'true')
        },

        sendEmail(e) {
            emailjs.sendForm('service_jzdt3mo', 'template_wzmntjt', e.target, 'u6_6yAHh5zCtFP39t', {
                name: this.form.name,
                email: this.form.email,
                company: this.form.company,
                message: this.form.message,
                savingSuccessful: false
            })
                .then(
                    //PRIKAZ PORUKE O USPJESNOM EDIT-ovanju
                    this.$store.dispatch("NOTIF_SUCCESS", {
                        poruka: this.$t('User_message.m140'),
                        position: "top-end",
                        timer: 1500,
                        action: "reload"
                    })
                );
        },
    },
}
</script>

<style>
/*
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;1,400&family=Plus+Jakarta+Sans:wght@700;800&display=swap');
*/
.public-loader {
    position: fixed;
    inset: 0;
    background: #f0f5ff;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.public-spinner {
    width: 48px;
    height: 48px;
    border: 5px solid rgba(0, 80, 201, 0.2);
    border-top-color: #1673af;
    border-radius: 50%;
    animation: spin 0.75s linear infinite;
}

@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');

*,
*::before,
*::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

html {
    scroll-behavior: smooth;
}

.public-page {
    font-family: 'DM Sans', sans-serif;
    color: #0a0e1a;
    background: #f0f5ff;
    overflow-x: hidden;
}

/* NAV */
.nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    padding: 20px 0;
    transition: all 0.3s ease;
    background-image: url('../../public/img/prviBlock.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
}

.nav.scrolled {
    background-image: url('../../public/img/prviBlock.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    backdrop-filter: blur(12px);
    box-shadow: 0 1px 24px rgba(0, 80, 201, 0.09);
    padding: 12px 0;
}

.nav-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.nav-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-right: 20px;
}

.nav-logo-img {
    height: 32px;
}

.nav-links {
    display: flex;
    list-style: none;
    gap: 32px;
}

.nav-links a {
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    text-decoration: none;
    transition: color 0.2s;
    font-weight: bold !important;
}

.nav-links a:hover {
    color: #D0D0D3;
}

.nav-btn {
    margin-left: 32px;
    font-size: 14px;
    font-weight: 600;
    color: #1574AF;
    background: linear-gradient(135deg, #DBE9FA, #DBE9FA);
    padding: 9px 22px;
    border-radius: 8px;
    text-decoration: none;
    box-shadow: 0 4px 14px rgba(0, 80, 201, 0.25);
    transition: transform 0.2s, box-shadow 0.2s;
}

.nav-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 20px rgba(0, 80, 201, 0.35);
}

/* HERO */
.hero {
    display: flex;
    padding: 120px;
    max-width: 1400px;
    margin: 0 auto;
    gap: 60px;
    position: relative;
    margin-bottom: -65px;
}

.hero-bg {
    position: fixed;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    background: #f0f5ff;
}

.orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
}

.orb1 {
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(0, 129, 202, 0.3), transparent);
    top: -150px;
    right: -100px;
}

.orb2 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(0, 80, 201, 0.2), transparent);
    bottom: -100px;
    left: -80px;
}

.grid-overlay {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(0, 80, 201, 0.04) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0, 80, 201, 0.04) 1px, transparent 1px);
    background-size: 52px 52px;
}

.hero-content {
    flex: 1;
    max-width: 540px;
}

.hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(0, 129, 202, 0.1);
    color: #1673af;
    font-size: 16px;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 100px;
    margin-bottom: 24px;
    letter-spacing: 0.3px;
}

.badge-dot {
    width: 7px;
    height: 7px;
    background: #1673af;
    border-radius: 50%;
    animation: bdot 2s infinite;
}

@keyframes bdot {

    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }

    50% {
        opacity: 0.4;
        transform: scale(1.4);
    }
}

.hero-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 40px;
    font-weight: 800;
    line-height: 1.1;
    letter-spacing: -1.5px;
    margin-bottom: 20px;
}

.nav-logo-text {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 800;
    font-size: 20px;
    color: #243f6f;
}

.hero-gradient {
    background: linear-gradient(90deg, #1e8fd4, #1673af);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 10px !important;
}

.hero-sub {
    font-size: 16px;
    line-height: 1.75;
    color: #4a5568;
    margin-bottom: 36px;
}

.hero-btns {
    display: flex;
    gap: 16px;
    align-items: center;
    margin-bottom: 48px;
}

.btn-primary {
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(135deg, #1e8fd4, #1673af);
    padding: 13px 30px;
    border-radius: 10px;
    text-decoration: none;
    box-shadow: 0 8px 24px rgba(0, 80, 201, 0.3);
    transition: transform 0.2s, box-shadow 0.2s;
    cursor: pointer;
    border: none;
}

.btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 80, 201, 0.4);
}

.btn-outline {
    font-size: 15px;
    font-weight: 500;
    color: #1673af;
    text-decoration: none;
    transition: opacity 0.2s;
}

.btn-outline:hover {
    opacity: 0.7;
}

.hero-stats {
    display: flex;
    align-items: top;
    gap: 22px;
}

.hstat {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.hstat-v {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 24px;
    font-weight: 800;
    color: #1673af;
}

.hstat-l {
    font-size: 13px;
    color: #718096;
}

.hstat-div {
    width: 1px;
    height: 36px;
    background: #dce8ff;
}

/* MOCK */
.hero-visual {
    flex: 1;
    max-width: 580px;
}

.mock-card {
    background: #fff;
    border-radius: 18px;
    box-shadow: 0 24px 80px rgba(0, 50, 150, 0.14);
    border: 1px solid rgba(0, 80, 201, 0.07);
    overflow: hidden;
}

.mock-topbar {
    background: linear-gradient(135deg, #1e8fd4, #1673af);
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.mock-dots {
    display: flex;
    gap: 6px;
}

.mock-dots span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.35);
}

.mock-label {
    font-size: 16px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
}

.mock-table-wrap {
    padding: 0 20px;
}

.mock-thead {
    display: grid;
    grid-template-columns: 90px 1fr 100px 80px 80px;
    padding: 12px 0 8px;
    font-size: 13px;
    font-weight: 700;
    color: #a0aec0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #eef2ff;
}

.mock-trow {
    display: grid;
    grid-template-columns: 90px 1fr 100px 80px 80px;
    padding: 11px 0;
    border-bottom: 1px solid #f4f7ff;
    font-size: 13px;
    color: #2d3748;
    align-items: center;
}

.inv-badge {
    font-size: 13px;
    font-weight: 700;
    color: #1673af;
    padding: 2px 7px;
    border-radius: 5px;
}

.loc {
    color: #718096;
    font-size: 13px;
}

.tkv {
    font-weight: 600;
    color: #1673af;
    font-size: 13px;
    margin-left: 10px;
}

.stag {
    font-size: 13px;
    font-weight: 600;
    padding: 2px 9px;
    border-radius: 100px;
    font-style: normal;
}

.stag-a {
    background: #e6f7ee;
    color: #16a34a;
}

.stag-i {
    background: #fef3c7;
    color: #d97706;
}

.stag-p {
    background: #fff3e0;
    color: #ea580c;
}

.mock-footer-bar {
    padding: 12px 20px;
    display: flex;
    gap: 10px;
    background: #f8faff;
    flex-wrap: wrap;
}

.mfb-chip {
    font-size: 13px;
    color: #4a5568;
    background: #fff;
    border: 1px solid #e2e8f0;
    padding: 5px 11px;
    border-radius: 7px;
}

/* SECTIONS */
.section-wrap {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 60px;
}

.section-head {
    text-align: center;
    margin-bottom: 60px;
}

.sec-badge {
    display: inline-block;
    background: rgba(0, 129, 202, 0.1);
    color: #1673af;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 5px 14px;
    border-radius: 100px;
    margin-bottom: 16px;
}

.sec-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 40px;
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -1px;
    margin-bottom: 14px;
}

.sec-sub {
    font-size: 16px;
    color: #718096;
    line-height: 1.7;
    max-width: 480px;
    margin: 0 auto;
}

/* FEATURES */
.features-section {
    background: #fff;
    padding: 50px 0;
}

.feat-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
}

.feat-card {
    background: #f8faff;
    border: 1px solid #e8eeff;
    border-radius: 16px;
    padding: 28px;
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
}

.feat-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0, 80, 201, 0.1);
    border-color: #c5d4ff;
}

.feat-icon {
    font-size: 30px;
    margin-bottom: 12px;
}

.feat-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;
}

.feat-desc {
    font-size: 16px;
    line-height: 1.65;
    color: #718096;
}

/* HOW */
.how-section {
    background: #f0f5ff;
    padding: 50px 0;
}

.steps-row {
    display: flex;
    align-items: flex-start;
    gap: 0;
    position: relative;
}

.step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;
    text-align: center;
}

.step-circle {
    width: 72px;
    height: 72px;
    background: linear-gradient(135deg, #1e8fd4, #1673af);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-bottom: 16px;
    box-shadow: 0 8px 24px rgba(0, 80, 201, 0.25);
}

.step-emoji {
    font-size: 28px;
}

.step-num {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 22px;
    height: 22px;
    background: #fff;
    border: 2px solid #1673af;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 800;
    color: #1673af;
    display: flex;
    align-items: center;
    justify-content: center;
}

.step-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 6px;
}

.step-desc {
    font-size: 14px;
    color: #718096;
    line-height: 1.6;
    max-width: 150px;
}

.step-connector {
    position: absolute;
    top: 36px;
    right: -50%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #1e8fd4, #1673af);
    opacity: 0.2;
    z-index: 0;
}

/* STRIP */
.strip-section {
    background: linear-gradient(135deg, #1e8fd4, #1673af);
    padding: 60px 0;
}

.strip-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 32px;
}

.strip-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.strip-icon {
    font-size: 28px;
    flex-shrink: 0;
    margin-top: 2px;
}

.strip-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 5px;
}

.strip-desc {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.55;
}

/* CONTACT */
.contact-section {
    background: #fff;
    padding: 50px 0;
}

.contact-wrap {
    display: flex;
    gap: 80px;
    align-items: flex-start;
}

.contact-left {
    flex: 1;
    max-width: 420px;
}

.contact-left .sec-title {
    text-align: left;
}

.contact-left .sec-sub {
    text-align: left;
    margin: 0 0 28px;
}

.contact-left .sec-badge {
    display: inline-block;
}

.contact-info-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 12px;
}

.cinfo {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: #4a5568;
    font-weight: 500;
}

.cinfo span:first-child {
    font-size: 18px;
}

.contact-right {
    flex: 1;
}

.contact-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-group label {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
}

.form-group input,
.form-group textarea {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: #1a1a2e;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    padding: 11px 14px;
    outline: none;
    transition: border-color 0.2s;
    resize: vertical;
    background: #f8faff;
}

.form-group input:focus,
.form-group textarea:focus {
    border-color: #1673af;
    background: #fff;
}

.form-submit {
    align-self: flex-start;
    color: white !important;
}

/* FOOTER */
.footer-bar {
    background: #0a0e1a;
    height: 80px;
    display: flex;
    align-items: center;
}

.footer-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 60px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.footer-left {
    display: flex;
    align-items: center;
    gap: 14px;
}

.footer-logo-img {
    height: 22px;
    filter: brightness(0) invert(1);
    opacity: 0.6;
}

.footer-name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 800;
    font-size: 16px;
    color: rgba(255, 255, 255, 0.7);
}

.footer-sep {
    color: rgba(255, 255, 255, 0.2);
}

.footer-tagline {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.35);
}

.footer-copy {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.3);
}

/* ============================================
   MOBILE RESPONSIVE — dodati na kraj style bloka
   ============================================ */

/* Hamburger dugme */
.nav-hamburger {
    display: none;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    z-index: 1100;
}

.nav-hamburger span {
    display: block;
    width: 24px;
    height: 2px;
    background-color: #fff;
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;
}

.nav-hamburger.open span:nth-child(1) {
    transform: translateY(7px) rotate(45deg);
}

.nav-hamburger.open span:nth-child(2) {
    opacity: 0;
    transform: scaleX(0);
}

.nav-hamburger.open span:nth-child(3) {
    transform: translateY(-7px) rotate(-45deg);
}

/* Mobile overlay menu */
.nav-mobile-menu {
    display: none;
    position: fixed;
    top: 64px;
    right: 12px;
    width: 220px;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(16px);
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    box-shadow: 0 12px 40px rgba(0, 50, 150, 0.15);
    z-index: 1050;
    flex-direction: column;
    padding: 10px;
    opacity: 0;
    pointer-events: none;
    transform: translateY(-8px);
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.nav-mobile-menu.open {
    opacity: 1;
    pointer-events: all;
    transform: translateY(0);
}

.nav-mobile-menu ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0;
}

.nav-mobile-menu ul li a {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #1a1a2e;
    text-decoration: none;
    padding: 10px 14px;
    border-radius: 8px;
    transition: background 0.15s, color 0.15s;
}

.nav-mobile-menu ul a:hover {
    color: #1e8fd4;
}

.nav-mobile-menu ul .btn-primary {
    font-size: 16px;
    padding: 13px 36px;
    margin-top: 8px;
}

@media (max-width: 768px) {

    /* NAV */
    .nav-links,
    .nav-btn {
        display: none !important;
    }

    .nav-hamburger {
        display: flex;
    }

    .nav-mobile-menu {
        display: flex;
    }

    .nav-inner {
        padding: 0 20px;
    }

    /* HERO */
    .hero {
        flex-direction: column;
        padding: 100px 20px 60px;
        gap: 40px;
        min-height: auto;
        align-items: center;
    }

    .hero-content {
        max-width: 100%;
        text-align: center;
    }

    .hero-title {
        font-size: 34px;
        letter-spacing: -1px;
    }

    .hero-badge {
        font-size: 14px;
    }

    .hero-sub {
        font-size: 15px;
    }

    .hero-btns {
        justify-content: center;
        flex-wrap: wrap;
        gap: 12px;
    }

    .hero-stats {
        justify-content: center;
        gap: 20px;
    }

    .hstat-v {
        font-size: 20px;
    }

    .hero-visual {
        max-width: 100%;
        width: 100%;
    }

    /* MOCK TABLE — sakrij kolone Lokacija i TKV na malim ekranima */
    .mock-thead,
    .mock-trow {
        grid-template-columns: 80px 1fr 72px;
    }

    .mock-thead span:nth-child(3),
    .mock-thead span:nth-child(5),
    .mock-trow span:nth-child(3),
    .mock-trow span:nth-child(5) {
        display: none;
    }

    /* FEATURES */
    .features-section {
        padding: 70px 0;
    }

    .feat-grid {
        grid-template-columns: 1fr;
        gap: 16px;
    }

    .section-wrap {
        padding: 0 20px;
    }

    .sec-title {
        font-size: 28px;
    }

    /* HOW IT WORKS */
    .how-section {
        padding: 70px 0;
    }

    .steps-row {
        flex-direction: column;
        align-items: center;
        gap: 32px;
    }

    .step-item {
        width: 100%;
        max-width: 300px;
    }

    .step-connector {
        display: none;
    }

    /* STRIP */
    .strip-section {
        padding: 50px 0;
    }

    .strip-grid {
        grid-template-columns: 1fr 1fr;
        gap: 24px;
    }

    /* CONTACT */

    .contact-right {
        max-width: 100%;
        width: 100%;
    }

    .contact-form {
        width: 100%;
    }

    .contact-form input,
    .contact-form textarea {
        width: 100%;
        box-sizing: border-box;
    }

    .form-submit {
        width: 100%;
        text-align: center;
        justify-content: center;
    }

    .contact-section {
        padding: 70px 0;
    }

    .contact-wrap {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 48px;
    }

    .contact-left {
        max-width: 560px;
        width: 100%;
        text-align: center;
    }

    .contact-left .sec-title {
        font-size: 28px;
        text-align: center;
    }

    .form-row {
        grid-template-columns: 1fr;
    }

    /* FOOTER */
    .footer-bar {
        height: auto;
        padding: 20px 0;
    }

    .footer-inner {
        flex-direction: column;
        gap: 10px;
        padding: 0 20px;
        text-align: center;
    }

    .footer-left {
        flex-wrap: wrap;
        justify-content: center;
    }

    .nav-logo-text {
        font-size: 26px;
    }

    .nav-logo-img {
        height: 42px;
    }

    .nav-hamburger {
        position: absolute;
        right: 20px;
    }

    /* OSTALO */
    .hstat-l {
        font-size: 14px;
    }

    .feat-desc {
        font-size: 14px;
    }

    .step-desc {
        font-size: 14px;
    }

    .strip-desc {
        font-size: 14px;
    }

    .mfb-chip {
        font-size: 13px;
    }

    .btn-primary:hover {
        box-shadow: 0 12px 32px rgba(0, 80, 201, 0.4);
        color: #fff !important;
    }

}

@media (max-width: 400px) {
    .hero-title {
        font-size: 28px;
    }

    .strip-grid {
        grid-template-columns: 1fr;
    }

    .hero-stats {
        flex-direction: column;
        align-items: center;
        gap: 16px;
    }

    .hstat-div {
        display: none;
    }

    .hero-btns {
        display: none;
    }
}
</style>